import { memo } from 'react';
// eslint-disable-next-line react/no-deprecated
import { hydrate } from 'react-dom';
import { Provider } from 'react-redux';
import * as Bowser from 'bowser';
import qs from 'query-string';
import { CssVarsProvider } from '@mui/joy/styles';

import { jsonParseSafe } from 'lib/utils';

import './app/lib/sentry';

import SoundUnlock from './app/lib/SoundUnlock';
import { AppContext, IAppContext } from './app/lib/AppContext';

import App from './app/App';
import { createStore } from './app/store';
import Theme from './app/ui/theme/Theme';

import './assets/fonts/avenir-next/connect.css';
import './assets/fonts/avenir-next-demi/connect.css';
import './assets/fonts/chalkboardse/connect.css';
import './assets/fonts/draw-with-markers/connect.css';
import './assets/fonts/georgia/connect.css';
import './assets/fonts/helvetica/connect.css';
import './assets/fonts/impact/connect.css';
import './assets/fonts/optima/connect.css';

import './normalize.css';
import './index.css';

SoundUnlock.init();

interface IApplication {
  context: IAppContext,
  store?: any,
}

const Application = memo((props: IApplication) => {
  const { context, store } = props;
  return (
    <CssVarsProvider theme={Theme} modeStorageKey="theme.type" colorSchemeStorageKey="theme.type">
      <Provider store={store}>
        <AppContext.Provider value={context}>
          <App />
        </AppContext.Provider>
      </Provider>
    </CssVarsProvider>
  );
});

const client = () => {
  const bowser = Bowser.parse(window.navigator.userAgent);
  const [, type, param, code] = window.location.pathname.split('/');
  const params = qs.parse(window.location.search);

  const context: IAppContext = {
    location: {
      pathname: document.location.pathname,
    },
    bowser,
    identity: {
      type,
      param,
      code,
    },
    mode: params?.mode === 'preview' ? params.mode : '',
    pages: jsonParseSafe<number[]>(params?.pages as string | undefined),
  };

  const store = createStore();

  hydrate(
    <Application context={context} store={store} />,
    document.getElementById('root'),
  );
};

const dev = async () => {
  const ReactDOM = await import('react-dom/client');
  const bowser = Bowser.parse(window.navigator.userAgent);
  const [, type, param, code] = window.location.pathname.split('/');
  const params = qs.parse(window.location.search);
  const context: IAppContext = {
    location: {
      pathname: document.location.pathname,
    },
    bowser,
    identity: {
      type,
      param,
      code,
    },
    mode: params?.mode === 'preview' ? params.mode : '',
    pages: jsonParseSafe<number[]>(params?.pages as string | undefined),
  };

  const store = createStore();

  const root = ReactDOM.createRoot(
    document.getElementById('root') as HTMLElement,
  );
  root.render(
    <Application context={context} store={store} />,
  );
};

const server = async (context: IAppContext) => {
  // @ts-ignore
  const ReactDOM = await import('react-dom/server');
  return {
    createStore,
    renderToString: (store: any) => ReactDOM.renderToString(
      <Application context={context} store={store} />,
    ),
  };
};

// // If you want your app to work offline and load faster, you can change
// // unregister() to register() below. Note this comes with some pitfalls.
// // Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorker.unregister();

export {
  client,
  server,
  dev,
};
